import { useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

/**
 * Venues scroll trigger hook for scroll-based venue slide transitions.
 * Creates pinned scroll animation with smooth slide transitions based on scroll position.
 */
export const useVenuesScrollTrigger = (containerRef) => {
  useEffect(() => {
    if (!containerRef.current) return;

    // Store the current ref value to use in cleanup
    const containerElement = containerRef.current;

    // Reset any existing transforms and styles to prevent refresh issues
    gsap.set(containerElement, { clearProps: "all" });
    
    const slides = gsap.utils.toArray('.venue-slide');
    const total = slides.length;

    // Reset all slides to initial state
    gsap.set(slides, { clearProps: "all" });
    
    // Initialize all slides as hidden except the first one
    gsap.set(slides, { opacity: 0, zIndex: 0 });
    gsap.set(slides[0], { opacity: 1, zIndex: 1 });

    // Create timeline for smooth transitions
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: containerElement,
        start: 'top top',
        end: () => `+=${window.innerHeight * total * 0.5}`,
        scrub: 0.5,
        pin: true,
        snap: {
          snapTo: 1 / (total - 1),
          duration: { min: 0.1, max: 0.3 },
          delay: 0.05,
          ease: 'power2.inOut',
        },
      },
    });

    // Create smooth transitions between slides
    slides.forEach((slide, i) => {
      const progress = i / (total - 1);
      
      if (i === 0) {
        // First slide starts visible
        tl.set(slide, { opacity: 1, zIndex: 1 }, progress);
      }
      
      if (i < total - 1) {
        const nextSlide = slides[i + 1];
        // Fade out current slide and fade in next slide
        tl.to(slide, { 
          opacity: 0, 
          duration: 0.15, 
          ease: 'power2.inOut' 
        }, progress)
        .to(nextSlide, { 
          opacity: 1, 
          duration: 0.15, 
          ease: 'power2.inOut' 
        }, progress);
      }
    });

    // Refresh ScrollTrigger on window resize to recalculate dimensions
    const handleResize = () => {
      ScrollTrigger.refresh();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      // Clean up specific ScrollTrigger instances for this component
      ScrollTrigger.getAll().forEach((st) => {
        if (st.trigger === containerElement) {
          st.kill();
        }
      });
      
      // Kill timeline
      tl.kill();
      
      // Reset all slides to prevent overlap on refresh
      gsap.set(slides, { clearProps: "all" });
      gsap.set(containerElement, { clearProps: "all" });
      
      // Remove any pin-spacer elements created by this ScrollTrigger
      const pinSpacers = document.querySelectorAll('.pin-spacer');
      pinSpacers.forEach(spacer => {
        if (spacer.contains(containerElement)) {
          spacer.remove();
        }
      });
      
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef]);
};
